import React, { useEffect } from "react";
import { Pagination } from "@life-line-lab/components-react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";
import { useTranslation } from "react-i18next";
import { useItemsPerPage } from ".";
import useQuery from "./query.hook";
import { useState } from "react";
import { isNil } from "lodash";
import { useHistory } from "react-router-dom";

interface UsePaginationProps {
  currentListCount: number;
  currentPath?: string;
  resetPageDeps?: any;
}

const usePagination = ({
  currentListCount,
  currentPath,
  resetPageDeps = null,
}: UsePaginationProps) => {
  const { t } = useTranslation();
  const query = useQuery();
  const initialPage = !isNil(query.get("page"))
    ? parseInt(query.get("page") as string)
    : 1;
  const history = useHistory();

  const [itemPerPage, setItemPerPage] = useItemsPerPage(
    "" + DEFAULT_MAX_PER_PAGE,
  );
  const initialQueryParams = {
    page: initialPage,
    offset: (initialPage - 1) * DEFAULT_MAX_PER_PAGE,
  };
  const [queryParams, setQueryParams] = useState(initialQueryParams);

  const updateURL = (page) => {
    // store page in URL
    if (currentPath) {
      // if path doesnt end with ?, give it ?, otherwise &
      const refPath = currentPath.includes("?")
        ? `${currentPath}&page=${page}`
        : `${currentPath}?page=${page}`;

      //history.push(refPath);
      history.replace(refPath);
    }
  };

  //dropdown
  const handleItemsPerPageChange = (value: string) => {
    // recalculate due to item per page change
    // 1. calculate the new page number using floor(old_offset / new_itemperpage) + 1
    // 2. calculate the new offset using (new_page * new_itemperpage) - new_itemperpage
    const new_itemperpage = parseInt(value);
    const old_offset = queryParams.offset;
    const new_page = Math.floor(old_offset / new_itemperpage) + 1;
    const new_offset = new_page * new_itemperpage - new_itemperpage;

    setQueryParams({ offset: new_offset, page: new_page });
    setItemPerPage(value);

    updateURL(new_page);
  };

  //algorithm for page change
  // suppose user sets 25 per page, and BE sends < 25. this shows that there is no more data to fetch
  // if BE sends 25, add a page. however this may present a blank page if there are no more data
  const handlePaginationChange = (value: any) => {
    let limit = itemPerPage;
    let page = queryParams.page;

    if (value === "NEXT") {
      if (currentListCount == itemPerPage) ++page; //to check how many items
      if (page > itemPerPage) page = itemPerPage;
    }

    if (value === "PREVIOUS") {
      --page;
      if (page < 1) page = 1;
    }

    const offset = (page - 1) * limit;
    setQueryParams({ ...queryParams, page, offset });

    updateURL(page);
  };

  const paginationComponent = (
    <Pagination
      previousIcon={<LeftOutlined />}
      nextIcon={<RightOutlined />}
      itemsPerPageValue={itemPerPage as number}
      itemsPerPageConfig={{
        initialValue: parseInt(DEFAULT_MAX_PER_PAGE),
        prefixLabel: t("label.show"),
        suffixLabel: t("txt.records"),
      }}
      onItemsPerPageChange={handleItemsPerPageChange}
      onChange={handlePaginationChange}
    />
  );

  useEffect(() => {
    if (resetPageDeps) {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        offset: 0,
      }));

      updateURL(1);
    }
  }, [JSON.stringify(resetPageDeps)]);

  return {
    paginationComponent,
    itemPerPage,
    queryParams,
    setQueryParams,
  };
};

export default usePagination;
